import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PlusCircle, Trash2 } from 'lucide-react';
import React from 'react';

const PLTCPInspectionFields = ({ findings, setFindings }) => {
   // Initialize empty tree entry
   const emptyTreeEntry = {
      species: '',
      numberOfTreesInventoried: 0,
      grossVolume: 0,
      netVolume: 0,
      treeCategory: 'Planted'
   };

   // Add new tree entry
   const addTreeEntry = () => {
      setFindings(prev => ({
         ...prev,
         pltcpFindings: {
            ...prev.pltcpFindings || {},
            treeInventory: [...(prev.pltcpFindings?.treeInventory || []), { ...emptyTreeEntry }]
         }
      }));
   };

   // Remove tree entry
   const removeTreeEntry = (index) => {
      // Prevent removing the last entry
      if (findings.pltcpFindings?.treeInventory?.length <= 1) {
         return;
      }

      setFindings(prev => ({
         ...prev,
         pltcpFindings: {
            ...prev.pltcpFindings || {},
            treeInventory: prev.pltcpFindings?.treeInventory.filter((_, i) => i !== index)
         }
      }));
      updateTotals();
   };

   // Update tree entry
   const updateTreeEntry = (index, field, value) => {
      setFindings(prev => {
         const newFindings = {
            ...prev,
            pltcpFindings: {
               ...prev.pltcpFindings || {
                  treeInventory: [{
                     species: '',
                     scientificName: '',
                     numberOfTreesInventoried: 0,
                     grossVolume: 0,
                     netVolume: 0,
                     treeCategory: 'Planted'
                  }]
               },
               treeInventory: [...(prev.pltcpFindings?.treeInventory || [])]
            }
         };

         // Update the specific field
         newFindings.pltcpFindings.treeInventory[index] = {
            ...newFindings.pltcpFindings.treeInventory[index],
            [field]: field === 'species' || field === 'treeCategory' || field === 'scientificName'
               ? value
               : parseFloat(value) || 0
         };

         // Calculate totals
         const totals = newFindings.pltcpFindings.treeInventory.reduce((acc, curr) => ({
            totalTrees: acc.totalTrees + (curr.numberOfTreesInventoried || 0),
            totalGrossVolume: acc.totalGrossVolume + (curr.grossVolume || 0),
            totalNetVolume: acc.totalNetVolume + (curr.netVolume || 0)
         }), { totalTrees: 0, totalGrossVolume: 0, totalNetVolume: 0 });

         // Update totals in findings
         newFindings.pltcpFindings = {
            ...newFindings.pltcpFindings,
            ...totals
         };

         return newFindings;
      });
   };

   return (
      <div className="space-y-6">
         <div>
            <Label className="text-base font-semibold">Tree Inventory</Label>
            <Table>
               <TableHeader>
                  <TableRow>
                     <TableHead>Species</TableHead>
                     <TableHead>Scientific Name</TableHead>
                     <TableHead>No. Of trees inventoried</TableHead>
                     <TableHead>Gross Volume (cu.m)</TableHead>
                     <TableHead>Net Volume (cu.m)</TableHead>
                     <TableHead>Tree Category</TableHead>
                     <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
               </TableHeader>
               <TableBody>
                  {findings?.pltcpFindings?.treeInventory?.map((tree, index) => (
                     <TableRow key={index}>
                        <TableCell>
                           <Input
                              value={tree.species || ''}
                              onChange={(e) => updateTreeEntry(index, 'species', e.target.value)}
                              placeholder="e.g., Mango"
                           />
                        </TableCell>
                        <TableCell>
                           <Input
                              value={tree.scientificName || ''}
                              onChange={(e) => updateTreeEntry(index, 'scientificName', e.target.value)}
                              placeholder="e.g., Mangifera indica"
                           />
                        </TableCell>
                        <TableCell>
                           <Input
                              type="number"
                              value={tree.numberOfTreesInventoried || ''}
                              onChange={(e) => updateTreeEntry(index, 'numberOfTreesInventoried', e.target.value)}
                              placeholder="0"
                           />
                        </TableCell>
                        <TableCell>
                           <Input
                              type="number"
                              step="0.001"
                              value={tree.grossVolume || ''}
                              onChange={(e) => updateTreeEntry(index, 'grossVolume', e.target.value)}
                              placeholder="0.000"
                           />
                        </TableCell>
                        <TableCell>
                           <Input
                              type="number"
                              step="0.001"
                              value={tree.netVolume || ''}
                              onChange={(e) => updateTreeEntry(index, 'netVolume', e.target.value)}
                              placeholder="0.000"
                           />
                        </TableCell>
                        <TableCell>
                           <Select
                              value={tree.treeCategory || 'Planted'}
                              onValueChange={(value) => updateTreeEntry(index, 'treeCategory', value)}
                           >
                              <SelectTrigger>
                                 <SelectValue placeholder="Select category" />
                              </SelectTrigger>
                              <SelectContent>
                                 <SelectItem value="Planted">Planted</SelectItem>
                                 <SelectItem value="Naturally grown">Naturally grown</SelectItem>
                              </SelectContent>
                           </Select>
                        </TableCell>
                        <TableCell>
                           <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => removeTreeEntry(index)}
                           >
                              <Trash2 className="h-4 w-4 text-red-500" />
                           </Button>
                        </TableCell>
                     </TableRow>
                  ))}
                  {/* Totals row */}
                  <TableRow className="font-bold">
                     <TableCell>Total</TableCell>
                     <TableCell>{findings?.pltcpFindings?.totalTrees || 0}</TableCell>
                     <TableCell>{findings?.pltcpFindings?.totalGrossVolume?.toFixed(3) || '0.000'}</TableCell>
                     <TableCell>{findings?.pltcpFindings?.totalNetVolume?.toFixed(3) || '0.000'}</TableCell>
                     <TableCell></TableCell>
                     <TableCell></TableCell>
                  </TableRow>
               </TableBody>
            </Table>

            <Button
               type="button"
               variant="outline"
               className="mt-4"
               onClick={addTreeEntry}
            >
               <PlusCircle className="h-4 w-4 mr-2" />
               Add Tree Entry
            </Button>
         </div>
      </div>
   );
};

export default PLTCPInspectionFields;